// src/pages/SettlementBatchesPage.tsx
//
// ★ PHASE 10 (Task 37) — Annex A stories 5, 6 and 8's own artefact, on a
// screen for the first time. `GET /api/settlement-batches` and
// `.../:id/members` (`services/gateway/src/routes/settlement-batches.ts`)
// read `PostgresOutboundFileRepository`, which has existed since the
// file-exchange branch with no UI over it at all.
//
// ⚠★ **THIS PAGE STOPPED BEING READ-ONLY ON 2026-08-17 (Wave 1b), and the
// paragraph it replaces is kept because its reasoning was right until it was
// not.** It read: *"Read-only, by design. A batch is produced by the emitting
// BATCH PROCESS and published by a separate publisher — neither is a screen
// action, so there is no 'create a batch' control here, only 'see what was
// sent'."* The premise was that a batch process existed. It does not:
// `emitSettlement` had ZERO non-test callers anywhere in a running deployment
// (docs/delivery/DEMO-READINESS-PLAN.md §1), so "produced by the emitting batch
// process" described something nobody had built, and the only settlement
// artefacts a customer could see were ones a fixture had inserted.
//
// **Release is now a screen action** (`POST /api/settlement-batches`), calling
// the same `emitSettlement` a batch process will call when one exists.
// PUBLICATION is still separate and still absent — an artefact is GENERATED
// here and reaches nobody, because no outbound transport is wired (Bucket B-3)
// — and the panel says so rather than letting "released" read as "sent".
//
// ⚠ Sortable: the WHOLE scoped population is fetched in one page (no
// pagination on this route — a deployment's settlement/outcome artefact
// count is small reference data, the same shape ContactsPage's own directory
// is), so the sort below operates over every row this session can see, not
// a truncated page of it.
// ══ ★★★ WAVE 8 (Task 8) — THE OUTCOME LEG BECAME CLICKABLE ON 2026-08-20 ══
//
// Wave 8 Task 1 gave `emitOutcome` its first real caller
// (`POST /api/outcome-files`) and proved an `HSP_OUTCOME-*.psv` leaves the
// process. **No screen reached it.** `RTM.md` declared that honestly as
// `⚠ NO SCREEN:` — and a leg that cannot be clicked does not demonstrate, because
// a demonstration is a person clicking.
//
// ⚠ **THIS PAGE IS THE HOME, AND TASK 1's OWN REASONING SAID IT SHOULD NOT BE.**
// That declaration argued: *"Building a control on `SettlementBatchesPage` would
// put an HSP-facing act on a PSD settlement screen and imply a handover that does
// not happen."* It is superseded rather than deleted, because half of it is still
// the design constraint this card is built around. What it got wrong is the
// premise: **this page is ALREADY the outcome artefact's screen.** Its own heading
// says *"Every settlement and outcome artefact your scope may see"*, `ARTEFACT_KIND_LABELS` renders `HSP_OUTCOME` as *"Outcome (to
// HSP)"*, and the table below lists them. Reading was here and producing was not,
// which is the split that had no argument behind it. What it got RIGHT is that
// "produced" must never read as "handed over" — so the honesty notice on the card
// is served by the SERVER, is the same sentence the route returns, and is on
// screen in every state including before anything is asked.
//
// ⚠ **The alternative homes, and why not.** `UploadPage` holds the delivery id in
// its hand the moment a delivery is processed — but an outcome file is an OUTBOUND
// artefact and that page is the intake, and putting it there would have left the
// artefact it produces on a different screen from the one that lists it. A new
// page would have been a third surface for one leg. What `UploadPage` gained
// instead is the delivery id, rendered with a link to here, so the id an officer
// needs is obtainable BY CLICKING rather than only from a shell.
import { plainCode, plainProse } from '../lib/plain-words'
import { CategoryBadge, CodeBadge, StateBadge } from '../components/StatusBadge'
import { Fragment, useEffect, useState } from 'react'
import { SgdsAlert, SgdsButton, SgdsSelect, SgdsSelectOption } from '@govtechsg/sgds-web-component/react'
import { targetValue } from '../lib/sgds-dom'
// ★★ ACCEPTANCE 3 — the control's visibility filters on an EXPORTED `@prs2/auth`
// PREDICATE, which is the rule `components/Shell.tsx`'s rail follows and the one
// this repository refuses to break on principle (`lib/authority.ts`'s header).
// `scopeAdmitsNothing` is the SAME function `routes/outcome-files.ts` guards its
// identity-level floor with — it moved from `@prs2/persistence` to `@prs2/auth` on
// 2026-08-20 precisely so a browser could call it, because that package builds SQL
// over `pg`. The alternative was a third copy of one sentence, and this repository
// has already paid for two (S-011).
//
// ⚠ **Hiding a control is presentation, never enforcement.** The route asks its
// own question against the session's real scope on every request, and where this
// card offers something the route then refuses, THE ROUTE'S OWN REASON is what
// renders — see `OUTCOME_REFUSAL_FRAME` below. Deleting this import would cost
// usability and zero security.
import type { OutcomeRefusalReason } from '@prs2/contracts'
import { scopeAdmitsNothing } from '@prs2/auth'
import { api, ApiError } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useSort, sortParams } from '../lib/useSort'
import { useAuth } from '../state/AuthContext'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { FormResult, type FormResultState } from '../components/FormResult'
import { formatCents, formatIsoDate } from '../lib/format'
import {
  isRateProvenanceMarked,
  rateProvenanceKeys,
  RateProvenanceNotes,
} from '../components/RateProvenance'
// ★★ WAVE 8 — the members read is PAGED (267,326 bytes measured, 5.35× the P2C3
// §7.1.6 cap). The ONE page-number control every offset-paged table in this app
// uses, never a copy: see `components/Pagination.tsx`'s own header.
import { useOffsetPager } from '../lib/useOffsetPager'
import { Pagination } from '../components/Pagination'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'
import type {
  OutcomeFileResponse,
  SettlementBatch,
  SettlementBatchMember,
  SettlementEmitResponse,
} from '../lib/types'
import { NETWORK_FAILURE } from '../lib/messages'

type BatchSortKey = 'id' | 'counterpartyCode' | 'artefactKind' | 'businessDate' | 'state' | 'recordCount' | 'totalPayableCents'

const COLUMN_LABELS: Readonly<Record<BatchSortKey, string>> = {
  id: 'Artefact Id',
  counterpartyCode: 'Counterparty',
  artefactKind: 'Kind',
  businessDate: 'Business date',
  state: 'State',
  recordCount: 'Records',
  totalPayableCents: 'Total payable',
}

const ARTEFACT_KIND_LABELS: Readonly<Record<string, string>> = {
  PA_SETTLEMENT: 'Settlement (to Payment Agency)',
  HSP_OUTCOME: 'Outcome (to HSP)',
}

/**
 * ★★ WAVE 8 (Task 8) — HOW EACH OF THE FOUR REFUSALS IS FRAMED, and why a frame
 * is not a second copy of the reason.
 *
 * ⚠ **The refusals must reach the officer AS REASONS, not as one opaque line.**
 * `POST /api/outcome-files` refuses **FIVE** ways (400 ×2, 403, 404, 409) and they need
 * three different next actions from the officer — correct the id, switch identity, or
 * wait for the ingest and change nothing about the form. ⚠ This read *"refuses four
 * ways"* until 2026-08-20; `SettlementBatchesPage.dom.test.tsx`'s own case is titled
 * *"the FIVE refusals are five DIFFERENT headings"* and has been since it landed.
 * FOUR of the five are reachable from this screen: the **403** is normally
 * suppressed by the visibility gate below, which withholds the control with a
 * reason instead, so an officer meets it over HTTP rather than by clicking. Rendering them all as "the request
 * was refused" and letting the officer read a paragraph to work out which is the
 * defect `FormResult.tsx`'s own header names one layer down.
 *
 * ⚠ **WHAT IS HERE IS NAVIGATION, AND NOTHING ELSE.** Not one entry restates a
 * fact the route decided: the sentence the officer reads is always
 * `ApiError.message`, verbatim, and the state that refused a 409 is read from the
 * route's own `deliveryState` field rather than parsed out of its prose. These
 * strings say only what to do next, which is the client's job and is not knowable
 * from the server's side of the wire.
 *
 * ⚠ **The 409 is the one that needed its own wording** (A-55, and the route's own
 * comment). It is the only refusal where the request was RIGHT: `fileScopePredicate`
 * admitted the delivery before its state was looked at, so the id is correct and
 * the identity is correct, and an officer told to "check the request" would go and
 * re-check a thing that is not wrong. It is also the only one whose remedy is
 * somebody else's — the ingest finishing, or an operator un-sticking a run that
 * died mid-file.
 *
 * ⚠ Keyed on the route's `reason` TOKEN, not on the status: `400` covers both "you
 * sent no id" and "that is not an id", which are different mistakes. Unrecognised
 * tokens fall through to {@link UNFRAMED_REFUSAL} rather than being dropped, so a
 * refusal this build has never seen still renders the server's own sentence.
 */
interface OutcomeRefusalFrame {
  /** A heading that says which KIND of thing went wrong. Never the reason itself. */
  readonly title: string
  /** What to do next. Prepended to the server's own sentence in the announced result. */
  readonly lead: string
}

/**
 * ★★ KEYED ON THE SHARED CONTRACT, NOT ON STRING LITERALS OF ITS OWN.
 *
 * ⚠ **Nothing kept these five keys in step with the route's five `reason` tokens
 * until 2026-08-20.** They matched — checked by hand, twice — and a rename on the
 * route would have left EVERY DOM case in this page's suite passing, because each
 * mints its OWN token in a stubbed response instead of importing the route's,
 * while the officer silently dropped to {@link UNFRAMED_REFUSAL}: the heading
 * gone, and the 409's *wait, do not correct* lead gone with it. That makes the
 * acceptance criterion *"all five refusals render as themselves"* FALSE with the
 * whole suite green — the one failure this page's own tests cannot see. The two
 * with no end-to-end proof were the 403 and the 409, and the 409 is the refusal
 * this control was added for.
 *
 * The type is now TOTAL over {@link OutcomeRefusalReason} (`@prs2/contracts`,
 * `outcome-refusal.ts`), which both this bundle and the route depend on: a token
 * added to the contract with no frame here is a TYPE ERROR, and a key here that
 * the union does not carry is a type error too.
 * `SettlementBatchesPage.dom.test.tsx` asserts the same coverage at RUNTIME as
 * well, because a type-level guarantee evaporates the moment somebody widens
 * something back to `string` to silence an error.
 */
export const OUTCOME_REFUSAL_FRAME: Readonly<Record<OutcomeRefusalReason, OutcomeRefusalFrame>> = {
  INBOUND_FILE_REQUIRED: {
    title: 'No delivery was named',
    lead: 'Nothing was produced. Enter the delivery id and ask again.',
  },
  INBOUND_FILE_ID_MALFORMED: {
    title: 'That is not a delivery id',
    lead: 'Nothing was produced. Correct the id and ask again.',
  },
  NOT_A_PARTY_TO_ANY_COUNTERPARTY: {
    title: 'This is about your identity, not the delivery',
    lead:
      'Nothing was produced, and no id you could type would change this answer. Signing in as an identity ' +
      'that holds a counterparty grant is what changes it.',
  },
  NO_SUCH_INBOUND_DELIVERY: {
    title: 'No such delivery is available to your identity',
    lead:
      'Nothing was produced. Check the id, and if it is right, check which identity you are signed in as, ' +
      'because those two are deliberately one answer here.',
  },
  DELIVERY_NOT_ANSWERABLE: {
    title: 'That delivery cannot be answered',
    // ★★ NARROWED 2026-08-20. This lead read: "Nothing was produced, and this is NOT
    // a request to correct: THE DELIVERY YOU NAMED IS ONE YOUR IDENTITY IS A PARTY
    // TO, AND THE ID IS RIGHT. Its own state is what refused, ..."
    //
    // ⚠ **Those two capitalised clauses were SERVER-SIDE FACTS INFERRED FROM A
    // STATUS CODE.** They are true only because `fileScopePredicate` admits the
    // delivery inside `openOutcome` BEFORE its state is read, so a 409 happens to
    // be reachable only for a delivery the caller is a party to. That is a fact
    // about branch ORDERING inside a statement this bundle cannot see, restated on
    // the client in English — the S-011 shape, and precisely what this page refuses
    // to do with an authorisation rule anywhere else. Reorder the route and the
    // client goes on asserting it, confidently and wrongly.
    //
    // What a 409 DOES entitle a client to say is that there is nothing to correct
    // on this form. The route already carries the fact itself: `deliveryState` is a
    // FIELD on the 409 body, and the server's own sentence — appended after this
    // lead, verbatim, in the announced live region — names the state that refused.
    lead:
      'Nothing was produced, and there is nothing on this form to correct: the refusal is about the ' +
      "delivery's own state, which the server names below. This is an ingest to wait for, or an " +
      'operator to look at.',
  },
}

/**
 * ⚠ The fallback, and it is a real branch rather than defensive decoration. A
 * gateway older or newer than this bundle can refuse with a token this build has
 * never heard of, and the one thing that must not happen is the officer losing the
 * server's sentence — so the frame degrades and the reason does not.
 */
const UNFRAMED_REFUSAL: OutcomeRefusalFrame = {
  title: 'The request was refused',
  lead: 'Nothing was produced.',
}

/** One refusal, as the card renders it. */
interface OutcomeRefusal {
  readonly frame: OutcomeRefusalFrame
  /** The server's own sentence, verbatim. */
  readonly message: string
  /**
   * The delivery's state, when the route sent one (its 409 does).
   *
   * ⚠ Taken from the response's own `deliveryState` field, NEVER read out of the
   * message. The route already knows this and already publishes it; recovering it
   * from prose would be a second copy of the fact, kept in step by nobody.
   */
  readonly deliveryState: string | null
}

/**
 * Is this token one the frame table knows?
 *
 * ⚠ A NARROWING, not a cast. `error.reason` arrives as `string | null` from the
 * wire and the frame table is now TOTAL over {@link OutcomeRefusalReason}, so
 * indexing it with a bare `string` is a type error — correctly: a gateway newer
 * than this bundle can send a token this build has never heard of, and that case
 * must reach {@link UNFRAMED_REFUSAL} rather than `undefined` dressed as a frame.
 * An `as` here would compile and lie.
 */
function isFramedRefusal(reason: string | null): reason is OutcomeRefusalReason {
  return reason !== null && Object.prototype.hasOwnProperty.call(OUTCOME_REFUSAL_FRAME, reason)
}

function outcomeRefusalOf(error: ApiError): OutcomeRefusal {
  const state = error.detail['deliveryState']
  return {
    frame: isFramedRefusal(error.reason) ? OUTCOME_REFUSAL_FRAME[error.reason] : UNFRAMED_REFUSAL,
    message: error.message,
    deliveryState: typeof state === 'string' && state.length > 0 ? state : null,
  }
}

/*
  ★★★ `sortBatches` USED TO LIVE HERE, AND ITS REMOVAL IS A CORRECTNESS FIX.

  It sorted `batches.data.files` in the browser — and `GET /api/settlement-batches`
  returns at most `DEFAULT_OUTBOUND_FILE_LIMIT` (100) artefacts, with no cursor and
  no offset. So every header on the table below ordered the hundred artefacts in
  hand while looking exactly like an answer about the estate: "the largest
  settlement batch" meant "the largest of the hundred most recent", silently, with
  no symptom on any estate holding a hundred or fewer.

  ⚠ **The comparator itself was correct.** It even put nulls last in both
  directions, which is the rule the server half now implements. That is the part
  worth remembering: the defect was not in the sorting, it was in what the sorted
  SET was, and no amount of care inside a comparator could have reached it. This is
  the exact case `apps/officer-web/src/sortable-header-census.test.ts` refuses
  mechanically for `useClientSort` — and it could not have caught this one, because
  a hand-rolled comparator is invisible to a check that looks for the hook.

  The sort is now `?sort=`/`?dir=` against `OUTBOUND_FILE_SORT_COLUMNS`, and the
  screen states the cap the server publishes, because a sort that changes WHICH
  hundred artefacts you see must not be silent about it.
*/


/**
 * ★★★ ADDED 2026-08-24 BY THE SORTABLE-HEADER SWEEP — EIGHT plain headers, the
 * largest unsortable table in the product, on the surface an officer reconciles a
 * settlement against.
 *
 * ⚠ Sorted on the SERVER: the membership is offset-paged and runs past a thousand
 * rows for a real batch, so ordering the page in hand would answer "the largest line
 * in these fifty" while looking like "the largest line in this batch".
 */
type MemberSortKey =
  | 'billReference'
  | 'hspAgencyCode'
  | 'officerId'
  | 'serviceDate'
  | 'payableToHsp'
  | 'subsidy'
  | 'coPayment'
  | 'appliedRuleId'

const MEMBER_COLUMN_LABELS: Readonly<Record<MemberSortKey, string>> = {
  billReference: 'Bill reference',
  hspAgencyCode: 'HSP agency',
  officerId: 'Officer',
  serviceDate: 'Service date',
  payableToHsp: 'Payable to HSP',
  subsidy: 'Subsidy',
  coPayment: 'Co-payment',
  appliedRuleId: 'Rule',
}

function MembersPanel({ fileId }: { fileId: string }) {
  /**
   * ★★ THE PAGE OFFSET — 2026-08-20 (Wave 8). This panel used to render the WHOLE
   * membership of a settlement artefact, and the response measured 267,326 bytes on
   * the demonstration estate's largest batch: 5.35× the 50 KB P2C3 §7.1.6 states.
   *
   * ⚠ Held HERE rather than inside `useOffsetPager` because the FETCH is keyed on it
   * and the hook is deliberately unaware of fetching — the same shape `ContactsPage`
   * and six sibling screens carry. The hook owns the page NUMBER; this component owns
   * the offset it fetches with, and they are kept in step in exactly one place.
   */
  const [pagerOffset, setPagerOffset] = useState(0)
  const memberSort = useSort<MemberSortKey>()
  const members = useApi(
    () => api.settlementBatchMembers(fileId, pagerOffset, sortParams(memberSort.sort)),
    [fileId, pagerOffset, memberSort.sort.column, memberSort.sort.dir],
  )
  const rows = members.data?.members ?? []
  /**
   * ⚠ **THE PAGE SIZE COMES FROM THE SERVER**, never from a constant in this file.
   * The gateway derives it from a measured byte budget (`SETTLEMENT_MEMBER_PAGE_SIZE`
   * and the arithmetic in its docstring); a `PAGE_SIZE` here would be a second copy
   * of that arithmetic, and the copy that drifts is always the one that looks
   * derived. `1` while the first request is in flight keeps the page arithmetic from
   * dividing by zero, and the controls are disabled then anyway — the same choice
   * `AccessControlMatrixPage` makes.
   */
  const pageSize = members.data?.paging.limit ?? 1
  const total = members.data?.paging.total
  const pager = useOffsetPager(pageSize, total)
  /*
    ⚠ A SORT CHANGE RESTARTS THE WALK. Page three of one order is not page three of
    another, so leaving the offset alone would land the reader in the middle of a
    membership they have not seen the top of — and both halves are reset in the same
    handler because resetting only the pager leaves this component's fetch offset
    stale for exactly one render, which is one wasted request.
  */
  const onMemberSort = (column: MemberSortKey) => {
    memberSort.onSort(column)
    pager.reset()
    setPagerOffset(0)
  }

  // ⚠ The pager's OWN movements — Next, Last, and the clamp `useOffsetPager` applies
  // when a population shrinks under a walk. There is no earlier point to write the
  // offset from, and this panel has no filter or sort to reset on.
  useEffect(() => {
    if (pager.offset !== pagerOffset) setPagerOffset(pager.offset)
  }, [pager.offset, pagerOffset])

  // ⚠ A different artefact is a different population. Without this, expanding a
  // second batch while on page 4 of the first asks for page 4 of the new one.
  useEffect(() => {
    setPagerOffset(0)
  }, [fileId])

  // WHICH ROWS' SUBSIDY CAME FROM A RATE THE AUTHORITY DID NOT SUPPLY.
  //
  // ⚠ The server publishes a bounded DICTIONARY (at most one entry per rule
  // version) and this matches each row against it on BOTH `ruleId` and `version`
  // — see `components/RateProvenance.tsx`. Marking on the id alone would mark a
  // confirmed `CPW:B1 v2` the day PSD supersedes the fabricated `v1`.
  //
  // ⚠★ **The dictionary is WHOLE-ARTEFACT, not this page** (changed with the paging
  // above): a page-scoped disclosure would let page 4 of a batch whose money rode a
  // fabricated CPW rate render with no note at all, and the officer reading it would
  // conclude the batch was clean. The cost is that a note can appear with no `[1]`
  // beside it on the page in view, which the heading below and the server's own
  // `pagingNote` state rather than leave to be puzzled over.
  //
  // ⚠ Not a filter and not a sort: the marker changes what a row SAYS, never
  // which rows are shown. A settlement batch's membership is the artefact's own
  // content and this screen may not quietly narrow it.
  const provenanceKeys = rateProvenanceKeys(members.data?.rateProvenance ?? [])

  return (
    <div className="pt-well p-4">
      <div className="flex items-center justify-between gap-3 mb-3 pt-no-print">
        <p className="text-sm font-semibold">Members of this artefact</p>
        {rows.length > 0 && (
          <div className="flex gap-2">
            {/* A plain navigation, not a fetch — the route answers with an
                `attachment` Content-Disposition, so the browser downloads the
                file and stays on this page. Same pattern AuditPage.tsx uses
                for its own two export links. */}
            <SgdsButton
              variant="outline"
              size="sm"
              type="button"
              onClick={() => {
                window.location.href = api.settlementBatchMembersExportUrl(fileId, 'pdf')
              }}
            >
              Download PDF
            </SgdsButton>
            <SgdsButton
              variant="outline"
              size="sm"
              type="button"
              onClick={() => {
                window.location.href = api.settlementBatchMembersExportUrl(fileId, 'xlsx')
              }}
            >
              Download Excel
            </SgdsButton>
          </div>
        )}
      </div>
      {members.error !== null ? (
        <ErrorCard title="Could not load members" message={members.error} onRetry={members.reload} />
      ) : members.initialLoading ? (
        <LoadingCard label="Loading members" rows={2} />
      ) : rows.length === 0 ? (
        <EmptyCard title="No members" message="Either this artefact has none, or it is outside your scope." />
      ) : (
        <div className="pt-table-wrap">
          <table className="pt-table">
            <caption className="sr-only">
              Bills that are members of this settlement artefact, one page at a time
            </caption>
            <thead>
              <tr>
                <SortableTh label={MEMBER_COLUMN_LABELS.billReference} sortKey="billReference" sort={memberSort.sort} onSort={onMemberSort} />
                <SortableTh label={MEMBER_COLUMN_LABELS.hspAgencyCode} sortKey="hspAgencyCode" sort={memberSort.sort} onSort={onMemberSort} />
                <SortableTh label={MEMBER_COLUMN_LABELS.officerId} sortKey="officerId" sort={memberSort.sort} onSort={onMemberSort} />
                <SortableTh label={MEMBER_COLUMN_LABELS.serviceDate} sortKey="serviceDate" sort={memberSort.sort} onSort={onMemberSort} />
                <SortableTh label={MEMBER_COLUMN_LABELS.payableToHsp} sortKey="payableToHsp" sort={memberSort.sort} onSort={onMemberSort} align="right" />
                <SortableTh label={MEMBER_COLUMN_LABELS.subsidy} sortKey="subsidy" sort={memberSort.sort} onSort={onMemberSort} align="right" />
                <SortableTh label={MEMBER_COLUMN_LABELS.coPayment} sortKey="coPayment" sort={memberSort.sort} onSort={onMemberSort} align="right" />
                {/*
                  ⚠ The cell shows the rule id, its VERSION and the rate together; the
                  sort is on the id alone, and rows sharing one fall to the server's
                  `bill_reference` tiebreaker rather than to the version. Sorting the
                  three as a composite would need a concatenation the whitelist
                  deliberately cannot express — see `SETTLEMENT_MEMBER_SORT_COLUMNS`.
                */}
                <SortableTh label={MEMBER_COLUMN_LABELS.appliedRuleId} sortKey="appliedRuleId" sort={memberSort.sort} onSort={onMemberSort} />
              </tr>
            </thead>
            <tbody>
              {rows.map((member: SettlementBatchMember) => (
                <tr key={member.billReference}>
                  <td className="pt-mono">{member.billReference}</td>
                  <td>{member.hspAgencyCode}</td>
                  <td className="pt-mono">{member.officerId}</td>
                  <td className="pt-tabular">{formatIsoDate(member.serviceDate)}</td>
                  <td className="pt-tabular pt-num">{formatCents(member.payableToHspCents)}</td>
                  <td className="pt-tabular pt-num">{formatCents(member.subsidyCents)}</td>
                  <td className="pt-tabular pt-num">{formatCents(member.copayCents)}</td>
                  <td className="pt-mono">
                    {member.appliedRuleId} v{member.appliedRuleVersion}
                    {isRateProvenanceMarked(provenanceKeys, member.appliedRuleId, member.appliedRuleVersion) && (
                      // ⚠ A MARKER ON THE FIGURE'S OWN ROW, resolved by the note
                      // block below the table — the footnote idiom, because
                      // repeating a ~470-character sentence on 610 of 1,059 rows
                      // would add ~290 KB to a payload the P2C3 §7.1.6 50 KB cap
                      // governs, and would be unreadable besides. The marker is
                      // per-row, so it is never a blanket page banner.
                      // ⚠★ AND THE MARKER ASSERTS NOTHING. The first draft of it
                      // did: its `title` read "This rate was not supplied by the
                      // Authority" — a provenance CLAIM written in a .tsx, which is
                      // the second-copy defect this whole change exists to remove.
                      // Caught by `src/rate-provenance-census.test.ts` on its first
                      // run. A footnote marker points; the footnote speaks, and the
                      // footnote is the database row's.
                      <span
                        className="ml-1 font-semibold text-[var(--sgds-body-color-default)]"
                        aria-label="see the rate provenance note below this table"
                        title="See the rate provenance note below this table."
                      >
                        [1]
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/*
            ★★ THE PAGE CONTROL — Wave 8. Renders nothing when the whole membership
            fits one page, so a small batch is unchanged. `pt-no-print` is the
            component's own, for the reason its header gives: a printed page cannot
            be paged and P2C1 §12.52 requires this surface printable.
          */}
          <Pagination pager={pager} label="members" total={total} shown={rows.length} />
          {/*
            ⚠ THE SERVER'S OWN SENTENCE, rendered and never re-authored here — what
            the walk guarantees, and that the notes below cover the whole artefact
            rather than this page. A screen-written version would be a second
            statement of a claim about the query, which is the defect
            `components/RateProvenance.tsx` exists to keep out of this app.
            Shown only where there is more than one page: on a single-page batch
            it would describe a walk nobody is taking.
          */}
          {pager.pageCount !== undefined && pager.pageCount > 1 && members.data !== undefined && (
            <p className="px-5 pb-3 text-sm text-[var(--sgds-color-subtle)] m-0 max-w-[85ch]">
              {plainProse(members.data.pagingNote)}
            </p>
          )}
          {/*
            The [1] marker's own resolution. Rendered INSIDE the table wrapper so
            it travels with the table on print (P2C1 §12.52) and cannot be
            scrolled away from the rows it explains. Empty — and therefore
            absent — on an MSO/CCS-only batch.

            ⚠ The heading says ARTEFACT and not "these rows" because the dictionary is
            whole-artefact now — see the comment on `provenanceKeys`.
          */}
          <RateProvenanceNotes
            provenance={members.data?.rateProvenance ?? []}
            heading="[1] Where the subsidy rates in this artefact came from"
          />
        </div>
      )}
    </div>
  )
}

export function SettlementBatchesPage() {
  const { sort, onSort, interacted } = useSort<BatchSortKey>()
  const [expanded, setExpanded] = useState<string | null>(null)
  /**
   * ★★★ THE PAGE OFFSET — 2026-08-24 (WAVE 18). Wave 17 moved this list's sort to the
   * server and had the screen SAY the table was a window onto a capped list; this is
   * the walk that makes it not one.
   *
   * ⚠ Held HERE rather than inside `useOffsetPager` because the FETCH is keyed on it
   * and the hook is deliberately unaware of fetching — the same shape `MembersPanel`
   * below and six sibling screens carry. The hook owns the page NUMBER; this component
   * owns the offset it fetches with, and they are kept in step in exactly one place.
   */
  const [batchOffset, setBatchOffset] = useState(0)
  // ⚠ Keyed on the sort AND the offset, so a header click re-queries rather than
  // re-ordering the page in hand — see the note where `sortBatches` used to be.
  const batches = useApi(
    () => api.settlementBatches({ ...sortParams(sort), offset: batchOffset }),
    [sort.column, sort.dir, batchOffset],
  )

  // ★★ WAVE 1b — the release control's own state.
  const [addressee, setAddressee] = useState('')
  const [releasing, setReleasing] = useState(false)
  const [released, setReleased] = useState<SettlementEmitResponse | null>(null)
  const [releaseError, setReleaseError] = useState<string | null>(null)

  // ⚠ The SERVER's order, verbatim. Nothing on this page re-sorts it.
  const rows = batches.data?.files ?? []
  const paging = batches.data?.paging
  /**
   * ⚠ **THE PAGE SIZE COMES FROM THE SERVER**, never from a constant in this file —
   * the same discipline `MembersPanel` states below. `1` while the first request is in
   * flight keeps the page arithmetic from dividing by zero, and the controls are
   * disabled then anyway.
   */
  const batchPager = useOffsetPager(paging?.limit ?? 1, paging?.total)
  useEffect(() => {
    if (batchPager.offset !== batchOffset) setBatchOffset(batchPager.offset)
  }, [batchPager.offset, batchOffset])
  /*
    ⚠ A SORT CHANGE RESTARTS THE WALK, and both halves are reset in the same handler.
    Page three of one order is not page three of another, so leaving the offset alone
    would land the reader in the middle of a list they have not seen the top of — and
    resetting only the pager leaves this component's fetch offset stale for exactly one
    render, which is one wasted request.
  */
  const onBatchSort = (column: BatchSortKey) => {
    onSort(column)
    batchPager.reset()
    setBatchOffset(0)
  }
  const mayEmit = batches.data?.mayEmit ?? false
  const addressees = batches.data?.addressableCounterparties ?? []

  // ⚠ Two clicks for a release, deliberately, and the same shape the approval
  // queue already uses. Releasing stamps every eligible bill in scope as
  // EMITTED and there is no un-release: `settled_at IS NULL` is what makes a
  // bill eligible, so the act is one-way. "Run outbox" shipping with no
  // confirmation at all is filed as a Wave 5 defect; this surface does not
  // repeat it.
  const [confirming, setConfirming] = useState(false)

  // ★★★ WAVE 8 (Task 8) — the outcome-file control's own state.
  const { user } = useAuth()
  const scope = user?.scope
  const [deliveryId, setDeliveryId] = useState('')
  // ⚠ Same two-click shape as the release above, and for a STRONGER reason: an
  // outcome file is `UNIQUE (inbound_file_id)` and must never re-render (ADR 0009
  // Decision 4), so the one produced here is the only one this delivery will ever
  // have. A release can at least be followed by another release.
  const [confirmingOutcome, setConfirmingOutcome] = useState(false)
  const [outcomeForm, setOutcomeForm] = useState<FormResultState>({ status: 'idle' })
  const [outcomeRefusal, setOutcomeRefusal] = useState<OutcomeRefusal | null>(null)
  const [produced, setProduced] = useState<OutcomeFileResponse | null>(null)

  async function produceOutcomeFile(): Promise<void> {
    setOutcomeForm({ status: 'submitting' })
    setOutcomeRefusal(null)
    setProduced(null)
    try {
      // ⚠ SENT UNTRIMMED-CHECKED AND UNVALIDATED, on purpose. The uuid shape is
      // the ROUTE's rule (`INBOUND_FILE_ID_PATTERN`) and `FieldError.tsx`'s header
      // is explicit that a syntax pattern stays on the server — a copy of it here
      // would be the second statement this repository refuses. So a blank field
      // and a mistyped id both reach the gateway and come back as its own 400s,
      // which is why both of those refusals are reachable by clicking at all.
      const response = await api.outcomeFile(deliveryId.trim())
      setProduced(response)
      setConfirmingOutcome(false)
      setOutcomeForm({
        status: 'success',
        message:
          response.outcome === 'ALREADY_GENERATED'
            ? `This delivery had already been answered. The artefact below is the one that exists, byte for ` +
              `byte: this was not a second batch. Produced, not sent: it has reached nobody.`
            : `Outcome file produced for ${response.counterpartyCode}: ${response.recordCount} record(s) ` +
              `answered. Produced, not sent: it has reached nobody.`,
      })
      // The artefact is now in this page's own list — re-read it rather than
      // leaving the officer looking at a table that predates their action.
      batches.reload()
    } catch (error: unknown) {
      if (error instanceof ApiError) {
        const refusal = outcomeRefusalOf(error)
        setOutcomeRefusal(refusal)
        setConfirmingOutcome(false)
        // ⚠ The announced sentence is the FRAME's next-action line followed by the
        // SERVER's own reason, verbatim and complete. One live region says the
        // whole thing, so a screen-reader user is not left with a heading they
        // cannot see and a paragraph they were never told about.
        setOutcomeForm({ status: 'error', message: `${refusal.frame.lead} ${refusal.message}` })
        return
      }
      setConfirmingOutcome(false)
      setOutcomeForm({
        status: 'error',
        message: `${NETWORK_FAILURE} No outcome file was produced.`,
      })
    }
  }

  async function release(): Promise<void> {
    if (addressee === '') return
    setReleasing(true)
    setReleaseError(null)
    setReleased(null)
    try {
      const outcome = await api.emitSettlementBatch(addressee)
      setReleased(outcome)
      setConfirming(false)
      // A release changes the list this page is showing, so re-read it rather
      // than leaving the officer looking at a table that predates their action.
      batches.reload()
    } catch (error: unknown) {
      setReleaseError(
        error instanceof ApiError
          ? error.message
          : `${NETWORK_FAILURE} Nothing was released.`,
      )
    } finally {
      setReleasing(false)
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">Settlement batches</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            Every settlement and outcome artefact your scope may see. Releasing
            produces an artefact from the approved bills your grants cover; <strong>publishing it to the Payment
            Agency is a separate step that this deployment cannot perform</strong>, because no outbound transport
            is wired.
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            ★★★ WAVE 27 — one control group, owner-directed. These answer a single
            question — "what do I want to do with this list?" — and were arranged as if
            they answered three or four. See `ExportToolbar` for the whole argument.
          */}
          <ExportToolbar
            onDownload={(format) => {
              window.location.href = api.settlementBatchExportUrl(format)
            }}
            email={<EmailReportButton path="/api/settlement-batches/export" />}
          />
        </div>
      </div>

      {/*
        ★★ WAVE 1b — the release control.

        ⚠ Rendered only once the list has loaded, because `mayEmit` and the
        addressee list come from that response. A button offered before the
        server has said whether it may be pressed is a button that reports its
        own refusal.
      */}
      {batches.data != null && (
        <div className="pt-card flex flex-col gap-4 pt-no-print">
          <h2 className="text-lg">Release a settlement</h2>
          {!mayEmit ? (
            // ⚠ Not a disabled button with no explanation — the Wave 5 defect
            // this surface must not repeat. The reason is stated and it names
            // the axis that refused.
            <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
              Your active identity holds no grant that can release a settlement, so this action is not offered.
              Releasing an approved bill to a Payment Agency needs a grant on the agency whose bills are being
              released.
            </p>
          ) : (
            <>
              <div className="max-w-[28rem]">
                {/* ★ WAVE 26 — the house `SgdsSelect`, not a native `<select>`. */}
                <SgdsSelect
                  label="Payment Agency"
                  value={addressee}
                  onSgdsChange={(e) => {
                    setAddressee(targetValue(e))
                    setReleased(null)
                    setReleaseError(null)
                    setConfirming(false)
                  }}
                >
                  <SgdsSelectOption value="">Choose a Payment Agency&hellip;</SgdsSelectOption>
                  {addressees.map((code) => (
                    <SgdsSelectOption key={code} value={code}>
                      {code}
                    </SgdsSelectOption>
                  ))}
                </SgdsSelect>
              </div>

              {/*
                ⚠ THE Q-30 GAP, in the server's own words. The screen must not
                imply that choosing correctly is enforced — nothing checks that
                this Payment Agency is the right one for the bills the release
                will carry, and the tender does not say.
              */}
              <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
                {plainProse(batches.data.addresseeNote)}
              </p>

              {confirming ? (
                <div className="flex flex-col gap-3">
                  <p className="font-semibold" role="alert">
                    Release every approved, unreleased bill your grants cover to {addressee}? This stamps each one
                    as released and <strong>cannot be undone</strong>: a released bill is no longer eligible for a
                    later batch.
                  </p>
                  <div className="flex gap-2">
                    <SgdsButton type="button" disabled={releasing} onClick={() => void release()}>
                      {releasing ? 'Releasing…' : 'Yes, release'}
                    </SgdsButton>
                    <SgdsButton variant="outline" type="button" disabled={releasing} onClick={() => setConfirming(false)}>
                      Cancel
                    </SgdsButton>
                  </div>
                </div>
              ) : (
                <div>
                  <SgdsButton type="button" disabled={addressee === ''} onClick={() => setConfirming(true)}>
                    Release settlement
                  </SgdsButton>
                </div>
              )}
            </>
          )}

          {releaseError !== null && (
            <div role="alert">
              <ErrorCard title="The release was refused" message={releaseError} />
            </div>
          )}

          {released !== null && (
            <div role="status" aria-live="polite" className="flex flex-col gap-2">
              {released.outcome === 'NOTHING_TO_EMIT' ? (
                // ⚠ "Nothing was eligible" is a DIFFERENT sentence from "that
                // did not work". The run happened and matched no bill, and no
                // empty artefact was committed — a Payment Agency receiving one
                // cannot tell a real empty cycle from a failed run.
                <p className="font-semibold">
                  Nothing was eligible. No artefact was created: every bill your grants cover has either not been
                  approved yet or has already been released.
                </p>
              ) : (
                <>
                  <p className="font-semibold text-[var(--sgds-success-color-emphasis)]">
                    Released {released.recordCount} bill(s) to {released.counterpartyCode},{' '}
                    {released.totalPayableCents === null ? '' : formatCents(released.totalPayableCents)} payable.
                  </p>
                  {/*
                    ★★★ WAVE 24 — WHAT THE RELEASE LEFT BEHIND, on the screen.

                    "Released 10000 bill(s)" was rendered over a release that had
                    hit its record bound with 2,400 bills still eligible. Nothing
                    here, in the response, or in the audit detail said so, and the
                    remainder stays APPROVED — so an operator releasing once per
                    business date left money unsettled for a cycle with no signal.

                    ⚠ The SERVER's sentence, verbatim, and `role="alert"`: this is
                    the one thing on a success panel that is not good news, and a
                    reader who has just seen a green line has stopped reading.
                  */}
                  {released.remainingNote !== undefined && (
                    <div role="alert" className="mt-2">
                      <SgdsAlert show variant="warning">
                        <span>{plainProse(released.remainingNote)}</span>
                      </SgdsAlert>
                    </div>
                  )}
                  <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
                    The artefact exists and its contents are sealed with a digest the database computed over the
                    rendered bytes. <strong>It has not been sent anywhere.</strong> Publication needs an outbound
                    transport, which no environment in this build wires.
                  </p>
                  {/*
                    ⚠ SECURITY.md S-063, in the server's own words. Reading an
                    artefact needs a grant on the Payment Agency it is addressed
                    to; releasing needs one on the agencies whose bills it
                    carries. They do not line up, and an officer who is not told
                    goes looking for a row that will never be there.
                  */}
                  {released.mayReadItBack === false && released.readBackNote !== undefined && (
                    <p className="text-sm font-semibold max-w-[70ch]">
                      {plainProse(released.readBackNote)}{' '}
                      <span className="pt-mono">{released.fileId}</span>
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/*
        ★★★ WAVE 8 (Task 8) — ANNEX A STORY 8's OUTCOME FILE, FROM A KEYBOARD.
        See this file's header for why this card is here and not on `UploadPage`
        or on a page of its own.
      */}
      {batches.data != null && (
        <div className="pt-card flex flex-col gap-4 pt-no-print">
          <h2 className="text-lg">Answer a provider delivery</h2>

          {/*
            ★★ THE STANDING HONESTY NOTICE. Rendered from the SERVER's own
            sentence (`outcomeHandoverNote`, the identical constant the route
            returns as `handoverNote`), OUTSIDE every conditional below, so it is
            on screen in every state this card can be in — before anything is
            asked, while a request is in flight, after a refusal, and after a
            success.

            ⚠ **It is not softened and it is not optional.** "Produced" must never
            be readable as "the provider has their file": nothing this deployment
            publishes reaches a provider, because a local-directory channel
            declares `collector: nobody`. A screen-authored version of this
            sentence would be the stale-honesty-notice defect (`docs/HANDOFF.md`
            §2.2 — *"a stale honesty notice is worse than none; it is a claim the
            code contradicts"*), which is why the server sends it and this shows
            it, exactly as `UploadPage` does with its scan notice.
          */}
          <p className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            <strong>Producing an outcome file is not sending one.</strong> {plainProse(batches.data.outcomeHandoverNote)}
          </p>

          {scope !== undefined && scopeAdmitsNothing(scope) ? (
            /*
              ⚠ Not a disabled button, and not a control that vanishes without a
              word — the Wave 5 defect the release control above also refuses to
              repeat. The axis that refused is named, and it is a fact about the
              identity rather than about anything on this form. The server would
              refuse this identically (403, `NOT_A_PARTY_TO_ANY_COUNTERPARTY`);
              not offering it saves a click that could only report its own
              refusal.
            */
            <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
              Your active identity holds no counterparty grant, so it is a party to no provider delivery and no
              outcome file could be produced for it. An outcome file answers one delivery, and only a party to
              that delivery&rsquo;s provider may ask for one.
            </p>
          ) : (
            <>
              <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
                An outcome file reports what PRS decided about every record in ONE delivery: including the
                records it rejected, which is the population this file exists for. There is
                exactly <strong>one per delivery, for ever</strong>: it cannot be re-rendered, corrected or
                replaced.
              </p>

              <label className="flex flex-col gap-1 max-w-[34rem]">
                <span className="font-semibold">Delivery id</span>
                <input
                  className="pt-input pt-mono"
                  type="text"
                  value={deliveryId}
                  aria-describedby="outcome-delivery-id-hint"
                  onChange={(e) => {
                    setDeliveryId(e.target.value)
                    setConfirmingOutcome(false)
                    setOutcomeForm({ status: 'idle' })
                    setOutcomeRefusal(null)
                    setProduced(null)
                  }}
                />
              </label>
              {/*
                ⚠ WHERE THE ID COMES FROM, said rather than assumed. Before Task 8
                the only way to hold one was a shell; `UploadPage` now renders the
                delivery id its own result already carried, with a link to here.
                ⚠ NO `pattern` and no length attribute: the shape is the route's
                rule, and a value invented here would be the second copy
                `FieldError.tsx`'s header refuses.
              */}
              <p id="outcome-delivery-id-hint" className="text-sm text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
                The id <em>Upload a delivery</em> reports once a delivery is processed, shown on that page with a
                link back here. A delivery must be <span className="pt-mono">PROCESSED</span> to be answerable.
              </p>

              {confirmingOutcome ? (
                <div className="flex flex-col gap-3">
                  <p className="font-semibold" role="alert">
                    Produce the outcome file for delivery{' '}
                    <span className="pt-mono">
                      {deliveryId.trim() === '' ? '(you have not entered one)' : deliveryId.trim()}
                    </span>
                    ? There is exactly one outcome file per delivery and it <strong>cannot be replaced</strong>.{' '}
                    Whatever it says about that delivery&rsquo;s records is what the provider would ever be
                    told.
                  </p>
                  <div className="flex gap-2">
                    <SgdsButton
                      type="button"
                      disabled={outcomeForm.status === 'submitting'}
                      onClick={() => void produceOutcomeFile()}
                    >
                      {outcomeForm.status === 'submitting' ? 'Producing…' : 'Yes, produce it'}
                    </SgdsButton>
                    <SgdsButton
                      variant="outline"
                      type="button"
                      disabled={outcomeForm.status === 'submitting'}
                      onClick={() => setConfirmingOutcome(false)}
                    >
                      Cancel
                    </SgdsButton>
                  </div>
                </div>
              ) : (
                <div>
                  {/*
                    ⚠ NEVER DISABLED on a blank or malformed field. Both are the
                    ROUTE's refusals (400 `INBOUND_FILE_REQUIRED` and 400
                    `INBOUND_FILE_ID_MALFORMED`), each with its own sentence, and a
                    client that silently disarmed the button would replace two
                    stated reasons with a control that does nothing and says
                    nothing — the Wave 5 defect — while also making two of this
                    route's FIVE refusals unreachable from any screen. ("four" here until
                    2026-08-20; the route refuses 400 ×2, 403, 404 and 409, and the two this
                    paragraph is about are the two 400s.)
                  */}
                  <SgdsButton type="button" onClick={() => setConfirmingOutcome(true)}>
                    Produce outcome file
                  </SgdsButton>
                </div>
              )}

              {/*
                ★ THE ANNOUNCED OUTCOME — the house pattern, unchanged: two live
                regions with fixed politeness, a refusal interrupting and a success
                waiting its turn (`components/FormResult.tsx`). The message it
                carries is the frame's next-action line followed by the SERVER's
                own reason, in full, so one announcement says the whole thing.
              */}
              <FormResult state={outcomeForm} />

              {outcomeRefusal !== null && (
                <div className="flex flex-col gap-1">
                  <p className="font-semibold m-0 text-[var(--sgds-danger-color-emphasis)]">
                    {outcomeRefusal.frame.title}
                  </p>
                  {outcomeRefusal.deliveryState !== null && (
                    /*
                      ⚠ The route's OWN `deliveryState`, never a word parsed out of
                      its sentence. This is the fact that tells an officer whether
                      to wait (RECEIVED), fetch an operator (CLAIMED — a run that
                      died mid-file) or stop (REJECTED — refused whole, carrying no
                      records at all, its reason on the delivery).
                    */
                    <p className="text-sm m-0">
                      The delivery&rsquo;s state: <span className="pt-mono">{outcomeRefusal.deliveryState}</span>
                    </p>
                  )}
                </div>
              )}

              {produced !== null && (
                <div className="flex flex-col gap-2">
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-2 max-w-[46rem] m-0">
                    <dt className="text-[var(--sgds-body-color-subtle)]">Delivery answered</dt>
                    <dd className="pt-mono m-0 break-all">{produced.inboundFileId}</dd>
                    <dt className="text-[var(--sgds-body-color-subtle)]">Provider</dt>
                    <dd className="m-0">{produced.counterpartyCode}</dd>
                    <dt className="text-[var(--sgds-body-color-subtle)]">Artefact id</dt>
                    <dd className="pt-mono m-0 break-all">{produced.fileId}</dd>
                    <dt className="text-[var(--sgds-body-color-subtle)]">Records answered</dt>
                    <dd className="pt-tabular pt-num m-0">{produced.recordCount}</dd>
                    {/*
                      ★ THE DIGEST the database computed over the rendered bytes at
                      COMMIT. It is on screen because it is the officer's only
                      handle on WHICH bytes exist: the operator sweep prints the
                      same value when it hands the file over, so the two can be
                      compared without either being trusted on its own.
                    */}
                    <dt className="text-[var(--sgds-body-color-subtle)]">Content digest</dt>
                    <dd className="pt-mono m-0 break-all">{produced.contentDigest}</dd>
                  </dl>
                  {produced.note !== undefined && <p className="text-sm m-0 max-w-[70ch]">{plainProse(produced.note)}</p>}
                  <p className="text-sm m-0 text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
                    The artefact is in the table below, as <strong>Outcome (to HSP)</strong>.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <SortAnnouncement sort={sort} interacted={interacted} busy={batches.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {batches.error !== null ? (
        <ErrorCard title="Could not load settlement batches" message={batches.error} onRetry={batches.reload} />
      ) : batches.initialLoading ? (
        <LoadingCard label="Loading settlement batches" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard title="No settlement batches in scope" message="Either none have been produced yet, or your active identity holds no grant covering one." />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap">
            <table className="pt-table">
              <caption className="sr-only">
                Settlement artefacts in your scope: one row per batch, with its members expandable beneath it
              </caption>
              <thead>
                <tr>
                  <SortableTh label="Artefact Id" sortKey="id" sort={sort} onSort={onBatchSort} />
                  <SortableTh label="Counterparty" sortKey="counterpartyCode" sort={sort} onSort={onBatchSort} />
                  <SortableTh label="Kind" sortKey="artefactKind" sort={sort} onSort={onBatchSort} />
                  <SortableTh label="Business date" sortKey="businessDate" sort={sort} onSort={onBatchSort} />
                  <SortableTh label="State" sortKey="state" sort={sort} onSort={onBatchSort} />
                  <SortableTh label="Records" sortKey="recordCount" sort={sort} onSort={onBatchSort} align="right" />
                  <SortableTh label="Total payable" sortKey="totalPayableCents" sort={sort} onSort={onBatchSort} align="right" />
                  <th scope="col" className="pt-no-print" data-unsortable="one control per row, not data — a sortable header over buttons would claim to order something with no order">
                    <span className="sr-only">Members</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((file) => (
                  <Fragment key={file.id}>
                    <tr>
                      <td className="pt-mono">{file.id.slice(0, 8)}&hellip;</td>
                      <td>{file.counterpartyCode}</td>
                      <td><CategoryBadge kind="code" value={file.artefactKind} label={ARTEFACT_KIND_LABELS[file.artefactKind] ?? plainCode(file.artefactKind)} /></td>
                      <td className="pt-tabular">{formatIsoDate(file.businessDate)}</td>
                      <td>
                        <StateBadge value={file.state} />
                      </td>
                      <td className="pt-tabular pt-num">{file.recordCount}</td>
                      <td className="pt-tabular pt-num">
                        {file.totalPayableCents === null ? 'None' : formatCents(file.totalPayableCents)}
                      </td>
                      <td className="pt-no-print">
                        <SgdsButton
                          variant="ghost"
                          size="sm"
                          type="button"
                          onClick={() => setExpanded((current) => (current === file.id ? null : file.id))}
                        >
                          {expanded === file.id ? 'Hide members' : 'Show members'}
                        </SgdsButton>
                      </td>
                    </tr>
                    {expanded === file.id && (
                      <tr>
                        <td colSpan={8} className="p-0">
                          <MembersPanel fileId={file.id} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            <p className="text-sm text-[var(--sgds-color-subtle)] m-0">
              {rows.length} artefact{rows.length === 1 ? '' : 's'} in your scope.{' '}
              {sort.column === null
                ? 'The server default order: newest first.'
                : `Sorted on the server by ${COLUMN_LABELS[sort.column]} (${sort.dir === 'asc' ? 'ascending' : 'descending'}).`}
            </p>
            {/*
              ★★★ THE SERVER'S OWN SENTENCE ABOUT THE PAGE — 2026-08-24 (WAVE 18), and
              it is UNCONDITIONAL now.

              ⚠ It used to render only when `paging.atCap` was true, and that
              conditionality was right for what it then SAID: *"your sort was answered
              over a subset"* is false on a list that came back short, and an
              unconditional version would have taught an officer to distrust a complete
              answer. Now that the list pages, the sentence states how many of how many
              are on screen — which is true in every state, so hiding it in any state
              would be the omission instead.

              ⚠ The words are still the SERVER's, so a change to the page size cannot
              leave this screen quoting the old one.
            */}
            {batches.data?.pagingNote !== undefined && (
              <p className="text-sm mt-2 m-0 text-[var(--sgds-color-subtle)]">{batches.data.pagingNote}</p>
            )}
          </div>
          <Pagination pager={batchPager} label="artefacts" total={paging?.total} shown={rows.length} />
        </div>
      )}
    </div>
  )
}
